import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 * 3. Cart updates (for dynamic pricing based on cart total)
 */
class ProductPrice extends HTMLElement {
  connectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);

    // Listen for cart updates to re-render price (for discount pricing based on cart total)
    document.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate);
  }

  disconnectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    document.removeEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate);
  }

  /**
   * Updates the price.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const newPrice = event.detail.data.html.querySelector('product-price [ref="priceContainer"]');
    const currentPrice = this.querySelector('[ref="priceContainer"]');

    if (!newPrice || !currentPrice) return;

    if (currentPrice.innerHTML !== newPrice.innerHTML) {
      currentPrice.replaceWith(newPrice);
    }
  };

  /**
   * Handles cart update events to refresh price display.
   * This is needed for dynamic pricing based on cart total (e.g., flat price discounts).
   */
  handleCartUpdate = async () => {
    // Skip if this price component is inside a dialog (e.g. quick-add modal).
    // Re-fetching using window.location.pathname would return the PDP product's
    // price instead of the quick-add product's price.
    if (this.closest('dialog')) return;

    const closestSection = this.closest('.shopify-section');
    if (!closestSection) return;

    const sectionId = closestSection.id?.replace('shopify-section-', '');
    if (!sectionId) return;

    try {
      const response = await fetch(`${window.location.pathname}?section_id=${sectionId}`);
      if (!response.ok) return;

      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      const newPrice = doc.querySelector('product-price [ref="priceContainer"]');
      const currentPrice = this.querySelector('[ref="priceContainer"]');

      if (!newPrice || !currentPrice) return;

      if (currentPrice.innerHTML !== newPrice.innerHTML) {
        currentPrice.innerHTML = newPrice.innerHTML;
      }
    } catch (error) {
      console.error('Failed to update product price on cart update:', error);
    }
  };
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}